import React, { useCallback, useContext, useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import useListado from '../../hooks/useListado.js';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import EmailModal from '../../components/common/EmailModal.jsx';
import { buildTablePdf, exportTableExcel, agregarLogosPdf } from '../../utils/exportUtils.js';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';
import { formatMoney as usd, formatDate, daysAgo } from '../finanzas-cxp/shared.js';

/**
 * Pantalla "Estado de Cuenta" (Clientes) / "Cuentas Por Pagar" (Proveedores), calcada de
 * `IbuFront/.../Admin/EstadoCuenta/EstadoCuenta.jsx` y `.../CXP/EstadoCuentaCXP/EstadoCuentaCXP.jsx`
 * (ambas son estructuralmente la misma pantalla, por eso comparten este componente; lo que
 * cambia va en `cfg`):
 *  - LISTA de TODOS los clientes/proveedores (50 por página, búsqueda, filtro Activo,
 *    columnas Nombre / Cargos / Abonos / Saldo / Estado Cuenta, fila de Totales de la
 *    página, PDF y Excel de la lista completa). V2 antes solo tenía un buscador.
 *  - Botón azul = modal con el kardex (movimientos) y filtro de fecha ("Saldo Anterior").
 *  - Botón rojo = modal "relacionado" (cargos/abonos relacionados, ordenado por fecha).
 *  - El saldo se muestra con el signo invertido (`-saldo`) igual que V1 (V1 pinta
 *    "$-755,229.97" para un cliente que debe y "$-0" para saldo cero).
 *  - Botón eMail (como V1) en la barra de la lista (PDF de la lista) y en el modal de
 *    kardex (PDF del estado de cuenta). El modal "relacionado" no lo tiene (igual que V1).
 *  - Todos los PDF llevan los logos de V1.
 */

const listaFmt = (n) => '$' + new Intl.NumberFormat('en-US').format(n);

// Arma el PDF del estado de cuenta (con logos como V1); lo guarda o lo manda por correo quien llama.
async function crearPdf({ lineas, head, body, foot, startY, fontSize }) {
  const doc = new jsPDF();
  await agregarLogosPdf(doc, { orientation: 'portrait' });
  lineas.forEach((texto, i) => doc.text(texto, 15, 30 + i * 7));
  autoTable(doc, {
    ...(fontSize ? { styles: { fontSize } } : {}),
    head: [head], body, foot, startY, showFoot: 'lastPage',
  });
  return doc;
}

const pie = (vacias, etiqueta, valor, colSpan) => [
  ...Array(vacias).fill(''),
  colSpan ? { content: etiqueta, colSpan, styles: { halign: 'left' } } : etiqueta,
  valor,
];

function ModalMovimientos({ cfg, modal, movimientos, cargando, error, fechaInicio, setFechaInicio, onClose }) {
  const { modo, row } = modal;
  const esRel = modo === 'relacionado';
  const nombre = row[cfg.nombreKey];
  const conCom = cfg.conComision;
  const saldoBadge = -(Number(row.saldo) || 0);
  const [mailAbierto, setMailAbierto] = useState(false);

  const neto = (m) => -(m.cargo || 0) - (conCom ? (m.comision || 0) : 0) + (m.abono || 0);

  const { filas, saldoInicial, cargosPeriodo, abonosPeriodo, saldoActual } = useMemo(() => {
    const base = esRel ? [...movimientos].sort((a, b) => (a.fecha > b.fecha ? 1 : -1)) : movimientos;
    const inicial = fechaInicio ? base.filter((m) => m.fecha < fechaInicio).reduce((s, m) => s + neto(m), 0) : 0;
    let saldo = inicial;
    let cargos = 0;
    let abonos = 0;
    const visibles = base
      .filter((m) => !fechaInicio || fechaInicio <= m.fecha)
      .map((m) => {
        saldo += neto(m);
        cargos -= (m.cargo || 0) + (conCom ? (m.comision || 0) : 0);
        abonos += m.abono || 0;
        return { ...m, saldoParcial: saldo };
      });
    return { filas: visibles, saldoInicial: inicial, cargosPeriodo: cargos, abonosPeriodo: abonos, saldoActual: saldo };
  }, [movimientos, fechaInicio, esRel, conCom]); // eslint-disable-line react-hooks/exhaustive-deps

  const columnas = esRel
    ? [
        { h: 'Fecha', c: (f) => formatDate(f.fecha) },
        { h: 'Descripción', c: (f) => f.descripcion },
        { h: 'Concepto', c: (f) => f.concepto },
        { h: 'Cargos', r: true, c: (f) => usd(f.cargo) },
        ...(conCom ? [{ h: 'Comisión', r: true, c: (f) => usd(f.comision) }] : []),
        { h: 'Abonos', r: true, c: (f) => usd(f.abono) },
        { h: 'Saldo Total', r: true, c: (f) => usd(f.saldoParcial) },
      ]
    : [
        { h: 'Fecha', c: (f) => formatDate(f.fecha) },
        { h: 'Movimiento', c: (f) => f.movimiento },
        { h: 'Número', c: (f) => f.numero },
        { h: 'Concepto', c: (f) => f.concepto },
        { h: 'Cargos', r: true, c: (f) => usd(f.cargo) },
        { h: 'Abonos', r: true, c: (f) => usd(f.abono) },
        ...(conCom ? [{ h: 'Comisión', r: true, c: (f) => usd(f.comision) }] : []),
        { h: 'Saldo', r: true, c: (f) => usd(f.saldoParcial) },
      ];

  const resumen = esRel
    ? [['Cargos', usd(cargosPeriodo)], ['Abonos', usd(abonosPeriodo)], ['Saldo Actual', usd(saldoActual)]]
    : [
        ['Saldo Anterior', usd(saldoInicial)],
        ['Cargos del Periodo', usd(cargosPeriodo)],
        ['Abonos del Periodo', (cfg.prefijoAbonosPeriodo || '') + usd(abonosPeriodo)],
        ['Saldo Actual', usd(saldoActual)],
      ];

  const archivo = `${cfg.archivoModal}-${nombre}`;
  const lineasPdf = [
    'Estado de Cuenta',
    `${cfg.entidad} ${nombre}`,
    ...(fechaInicio && (cfg.entidad === 'Proveedor' || esRel) ? [`Desde ${fechaInicio}`] : []),
  ];

  async function exportarPdf() {
    (await construirPdf()).save(`${archivo}.pdf`);
  }

  // eMail (solo kardex, como V1): manda el mismo PDF del estado de cuenta.
  async function enviarPorCorreo(email) {
    const doc = await construirPdf();
    await enviarCorreoAdjunto({
      subject: 'Estado de Cuenta',
      email,
      fileName: `EstadoCuenta-${nombre}.pdf`,
      docPdf: doc,
      textoHtml: plantillaCorreoV1(`Adjunto encontraras el Estado de Cuenta de ${nombre}.`),
    });
  }

  function construirPdf() {
    const cero = usd(0);
    if (!conCom) {
      // Proveedor (V1 EstadoCuentaCXP.jsx: PDFOC / PDFOCRelacionado)
      if (!esRel) {
        return crearPdf({
          lineas: lineasPdf, archivo, startY: 50,
          head: ['Fecha', 'Movimiento', 'Concepto', 'Numero', 'Cargos', 'Abonos', 'Saldo'],
          body: filas.map((f) => [f.fecha, f.movimiento, f.concepto, f.numero, usd(f.cargo), usd(f.abono), usd(f.saldoParcial)]),
          foot: [
            pie(4, 'Saldo Anterior', usd(saldoInicial), 2),
            pie(4, 'Cargos del Periodo', usd(cargosPeriodo), 2),
            pie(4, 'Abonos del Periodo', '-' + usd(abonosPeriodo), 2),
            pie(4, 'Saldo Actual', usd(saldoActual), 2),
          ],
        });
      } else {
        return crearPdf({
          lineas: lineasPdf, archivo, startY: 50, fontSize: 9,
          head: ['Fecha', 'Descripcion', 'Concepto', 'Cargos', 'Abonos', 'Saldo', 'Saldo Total'],
          body: filas.map((f) => [f.fecha, f.descripcion, f.concepto, usd(f.cargo), usd(f.abono), usd(f.cargo - f.abono), usd(-f.saldoParcial)]),
          foot: [pie(5, 'Cargos', usd(cargosPeriodo)), pie(5, 'Abonos', usd(abonosPeriodo)), pie(5, 'Saldo', usd(saldoActual))],
        });
      }
    }
    // Cliente (V1 EstadoCuenta.jsx: PDFOC / PDFOCRelacionado en su rama "Pendientes")
    if (!esRel) {
      const anterior = fechaInicio ? [['NA', 'Saldo Anterior', 'NA', cero, cero, cero, usd(saldoInicial)]] : [];
      return crearPdf({
        lineas: lineasPdf, archivo, startY: 50,
        head: ['Fecha', 'Movimiento', 'Concepto', 'Cargos', 'Comision', 'Abonos', 'Saldo'],
        body: [...anterior, ...filas.map((f) => [f.fecha, f.movimiento, f.concepto, usd(f.cargo), usd(f.comision), usd(f.abono), usd(f.saldoParcial)])],
        foot: [
          pie(4, 'Saldo Anterior', usd(saldoInicial), 2),
          pie(4, 'Cargos del Periodo', usd(cargosPeriodo), 2),
          pie(4, 'Abonos del Periodo', usd(abonosPeriodo), 2),
          pie(4, 'Saldo Actual', usd(saldoActual), 2),
        ],
      });
    }
    const pendientes = filas.filter((f) => f.saldo > 1);
    let saldo = saldoInicial;
    let cargos = 0;
    let abonos = 0;
    const body = pendientes.map((f) => {
      cargos += f.cargo + f.comision;
      abonos += f.abono;
      saldo = saldo - f.cargo - f.comision + f.abono;
      return [f.fecha, f.descripcion, f.concepto, usd(f.cargo), usd(f.comision), usd(f.abono), usd(f.abono - f.cargo - f.comision), usd(saldo)];
    });
    return crearPdf({
      lineas: lineasPdf, archivo, startY: fechaInicio ? 55 : 50, fontSize: 9,
      head: ['Fecha', 'Descripción', 'Concepto', 'Cargos', 'Comision', 'Abonos', 'Saldo', 'Saldo Total'],
      body: [['NA', 'Saldo Anterior', 'NA', cero, cero, cero, cero, usd(saldoInicial)], ...body],
      foot: [
        pie(6, 'Saldo Anterior', usd(saldoInicial)),
        pie(6, 'Cargos', usd(-cargos)),
        pie(6, 'Abonos', usd(abonos)),
        pie(6, 'Saldo Actual', usd(saldo)),
      ],
    });
  }

  function exportarExcel() {
    let cols;
    let datos;
    if (!conCom && !esRel) {
      cols = [['fecha', 'Fecha'], ['movimiento', 'Movimiento'], ['numero', 'Numero'], ['concepto', 'Concepto'], ['cargo', 'Cargo'], ['abono', 'Abono']];
      datos = filas;
    } else if (!conCom && esRel) {
      cols = [['fecha', 'Fecha'], ['descripcion', 'Descripcion'], ['concepto', 'Concepto'], ['cargo', 'Cargos'], ['abono', 'Abonos']];
      datos = filas;
    } else if (!esRel) {
      cols = [['fecha', 'Fecha'], ['movimiento', 'Movimiento'], ['numero', 'Numero'], ['concepto', 'Concepto'], ['cargo', 'Cargo'], ['abono', 'Abono'], ['comision', 'Comision']];
      datos = filas;
    } else {
      cols = [['fecha', 'Fecha'], ['descripcion', 'Descripcion'], ['concepto', 'Concepto'], ['cargo', 'Cargos'], ['comision', 'Comision'], ['abono', 'Abonos']];
      // V1 (rama "Pendientes"): solo movimientos con saldo pendiente, sin filtro de fecha.
      datos = movimientos.filter((m) => m.saldo > 1);
    }
    exportTableExcel(archivo, cols.map(([key, label]) => ({ key, label })), datos, { fileName: archivo, sheetName: cfg.hojaModal });
  }

  const nCols = columnas.length;
  const hayAnterior = fechaInicio && !esRel;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Estado de Cuenta de ${nombre}`}
      width="max-w-6xl"
      footer={<button type="button" className="btn-primary" onClick={onClose}>Cerrar</button>}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportarPdf} disabled={cargando} className="btn-outline text-xs px-3 py-1.5">PDF</button>
          {!esRel && (
            <button type="button" onClick={() => setMailAbierto(true)} disabled={cargando} className="btn-outline text-xs px-3 py-1.5">eMail</button>
          )}
          <button type="button" onClick={exportarExcel} disabled={cargando} className="btn-outline text-xs px-3 py-1.5">Excel</button>
        </div>
        <div className="flex items-center gap-2">
          <label className="label mb-0">Fecha</label>
          <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="input-base" />
        </div>
        <div className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          Saldo <span className="badge-danger text-base">{usd(saldoBadge)}</span>
        </div>
      </div>

      {error && <p className="text-sm text-danger-600 mb-2">{error}</p>}

      <div className="card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {columnas.map((c) => (
                <th key={c.h} className={`px-3 py-2 whitespace-nowrap ${c.r ? 'text-right' : 'text-left'}`}>{c.h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cargando && <tr><td colSpan={nCols} className="px-3 py-6 text-center text-slate-400">Cargando...</td></tr>}
            {!cargando && hayAnterior && (
              <tr>
                {columnas.map((c, i) => (
                  <td key={c.h} className={`px-3 py-2 whitespace-nowrap ${i === nCols - 1 ? 'text-right' : ''}`}>
                    {i === 1 ? 'Saldo Anterior' : i === nCols - 1 ? usd(saldoInicial) : 'NA'}
                  </td>
                ))}
              </tr>
            )}
            {!cargando && filas.map((f, i) => (
              <tr key={i}>
                {columnas.map((c) => (
                  <td key={c.h} className={`px-3 py-2 whitespace-nowrap ${c.r ? 'text-right' : ''}`}>{c.c(f)}</td>
                ))}
              </tr>
            ))}
            {!cargando && resumen.map(([etiqueta, valor]) => (
              <tr key={etiqueta} className="font-semibold">
                <td colSpan={nCols - 2} />
                <td className="px-3 py-2 text-right whitespace-nowrap">{etiqueta}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{valor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!esRel && (
        <EmailModal
          open={mailAbierto}
          onClose={() => setMailAbierto(false)}
          titulo="Enviar Estado de Cuenta"
          emailInicial={row.email || ''}
          onEnviar={enviarPorCorreo}
        />
      )}
    </Modal>
  );
}

export default function EstadoCuentaListado({ cfg }) {
  const { hasPermiso } = useContext(AuthContext);
  const [activo, setActivo] = useState('');
  const [exportando, setExportando] = useState(null);
  const [mailListaAbierto, setMailListaAbierto] = useState(false);
  const [modal, setModal] = useState(null);
  const [movs, setMovs] = useState([]);
  const [cargandoMovs, setCargandoMovs] = useState(false);
  const [errorMovs, setErrorMovs] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(daysAgo(60));

  const listar = useCallback(
    (params, opts) => apiClient.get(`/${cfg.route}`, { params, signal: opts?.signal }).then((r) => r.data),
    [cfg.route]
  );
  const {
    filas, paginacion, cargando, error, pagina, setPagina,
    sortBy, sortOrder, ordenarPor, busqueda, setBusqueda, cambiarFiltros,
  } = useListado({ listar, filtrosIniciales: { status: 'all' }, ordenPor: cfg.nombreKey, orden: 'asc', porPagina: 50 });

  const filtroActivo = (v) => (v ? { is_active: v } : { status: 'all' });

  function cambiarActivo(v) {
    setActivo(v);
    cambiarFiltros({ status: undefined, is_active: undefined, ...filtroActivo(v) });
  }

  async function abrir(modo, row) {
    setModal({ modo, row });
    setMovs([]);
    setErrorMovs(null);
    setCargandoMovs(true);
    try {
      const { data } = await apiClient.get(modo === 'kardex' ? cfg.kardexPath(row._id) : cfg.relacionadoPath(row._id));
      setMovs(data.data || []);
    } catch (e) {
      setErrorMovs(e.message);
    } finally {
      setCargandoMovs(false);
    }
  }

  async function traerFilasLista() {
    const { data } = await apiClient.get(`/${cfg.route}`, {
      params: { page: 1, limit: 100000, sortBy: cfg.nombreKey, sortOrder: 'asc', ...filtroActivo(activo) },
    });
    return (data.data || []).map((r) => ({
      nombre: r[cfg.nombreKey],
      cargos: Number(r.cargos) || 0,
      abonos: Number(r.abonos) || 0,
      saldo: -(Number(r.saldo) || 0),
    }));
  }

  // PDF de la lista completa (con logos); lo usan el botón PDF y el eMail de la barra.
  async function construirPdfLista() {
    const rows = await traerFilasLista();
    const suma = rows.reduce((t, r) => ({ cargos: t.cargos + r.cargos, abonos: t.abonos + r.abonos, saldo: t.saldo + r.saldo }), { cargos: 0, abonos: 0, saldo: 0 });
    const columnas = [
      { key: 'nombre', label: cfg.entidad },
      { key: 'cargos', label: 'Cargos', render: (r) => usd(r.cargos) },
      { key: 'abonos', label: 'Abonos', render: (r) => usd(r.abonos) },
      { key: 'saldo', label: 'Saldo', render: (r) => usd(r.saldo) },
    ];
    return buildTablePdf(cfg.tituloPdf, columnas, rows, {
      totalCols: { nombre: 'Totales', cargos: usd(suma.cargos), abonos: usd(suma.abonos), saldo: usd(suma.saldo) },
    });
  }

  async function exportarLista(tipo) {
    setExportando(tipo);
    try {
      if (tipo === 'pdf') {
        (await construirPdfLista()).save(`${cfg.archivoPdf}.pdf`);
      } else {
        const rows = await traerFilasLista();
        const columnas = [
          { key: 'nombre', label: cfg.entidad }, { key: 'cargos', label: 'Cargos' },
          { key: 'abonos', label: 'Abonos' }, { key: 'saldo', label: 'Saldo' },
        ];
        exportTableExcel(cfg.archivoExcel, columnas, rows, { fileName: cfg.archivoExcel, sheetName: cfg.archivoExcel });
      }
    } finally {
      setExportando(null);
    }
  }

  // eMail de la barra (V1 enviaMail): adjunta el PDF de la lista.
  async function enviarListaPorCorreo(email) {
    const doc = await construirPdfLista();
    await enviarCorreoAdjunto({
      subject: 'Estado de Cuenta',
      email,
      fileName: 'EstadoCuenta.pdf',
      docPdf: doc,
      textoHtml: plantillaCorreoV1(`Adjunto encontraras los saldos de los ${cfg.entidad === 'Cliente' ? 'clientes' : 'proveedores'}.`),
    });
  }

  const totales = useMemo(
    () => filas.reduce(
      (t, r) => ({ cargos: t.cargos + (Number(r.cargos) || 0), abonos: t.abonos + (Number(r.abonos) || 0), saldo: t.saldo - (Number(r.saldo) || 0) }),
      { cargos: 0, abonos: 0, saldo: 0 }
    ),
    [filas]
  );

  const boton = 'p-1.5 rounded-md text-white';
  const columnas = [
    { key: cfg.nombreKey, label: cfg.encabezadoNombre, sortable: true },
    { key: 'cargos', label: 'Cargos', sortable: true, render: (r) => listaFmt(Number(r.cargos) || 0) },
    { key: 'abonos', label: 'Abonos', sortable: true, render: (r) => listaFmt(Number(r.abonos) || 0) },
    { key: 'saldo', label: 'Saldo', sortable: true, render: (r) => listaFmt(-(Number(r.saldo) || 0)) },
    {
      key: '__acciones', label: cfg.encabezadoAcciones,
      render: (r) => (
        <div className="flex items-center gap-1">
          <button type="button" title="Estado de cuenta" aria-label="Estado de cuenta" className={`${boton} bg-primary-600 hover:bg-primary-700`} onClick={() => abrir('kardex', r)}>
            <FileText size={15} />
          </button>
          <button type="button" title="Estado de cuenta relacionado" aria-label="Estado de cuenta relacionado" className={`${boton} bg-danger-500 hover:bg-danger-600`} onClick={() => abrir('relacionado', r)}>
            <FileText size={15} />
          </button>
        </div>
      ),
    },
  ];

  const pieTabla = {
    [cfg.nombreKey]: <div className="text-right">Totales</div>,
    cargos: listaFmt(totales.cargos),
    abonos: listaFmt(totales.abonos),
    saldo: listaFmt(totales.saldo),
  };

  if (!hasPermiso(cfg.permiso)) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">{cfg.titulo}</h1>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => exportarLista('pdf')} disabled={exportando === 'pdf'} className="btn-outline text-xs px-3 py-1.5">
            {exportando === 'pdf' ? 'Generando...' : 'PDF'}
          </button>
          <button type="button" onClick={() => setMailListaAbierto(true)} className="btn-outline text-xs px-3 py-1.5">eMail</button>
          <button type="button" onClick={() => exportarLista('excel')} disabled={exportando === 'excel'} className="btn-outline text-xs px-3 py-1.5">
            {exportando === 'excel' ? 'Generando...' : 'Excel'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <label className="label">Activo</label>
          <select value={activo} onChange={(e) => cambiarActivo(e.target.value)} className="input-base">
            <option value="">Selecciona</option>
            <option value="Si">Activos</option>
            <option value="No">Inactivos</option>
          </select>
        </div>
        <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar..." className="input-base w-64" />
      </div>

      {error && <p className="text-sm text-danger-600">{error}</p>}

      <Pagination pagination={paginacion} page={pagina} onChange={setPagina} />
      <DataTable columns={columnas} data={filas} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor} footer={pieTabla} />
      <Pagination pagination={paginacion} page={pagina} onChange={setPagina} />

      <EmailModal
        open={mailListaAbierto}
        onClose={() => setMailListaAbierto(false)}
        titulo="Enviar Estado de Cuenta"
        onEnviar={enviarListaPorCorreo}
      />

      {modal && (
        <ModalMovimientos
          cfg={cfg} modal={modal} movimientos={movs} cargando={cargandoMovs} error={errorMovs}
          fechaInicio={fechaInicio} setFechaInicio={setFechaInicio} onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
